import './DecimalField.scss';
import './Field.scss';
import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { Decimal } from 'decimal.js-light';
import { pipe } from 'fp-ts/function';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CurrencyInput, { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Input, InputProps } from 'src/modules/common/components/Input';
import { DECIMAL_PLACES } from 'src/modules/common/constants/decimal';
import { getLocale } from 'src/modules/config/selectors/getLocale';
import { FormError } from 'src/modules/form/types/FormError';

type BaseProps<TData> = Omit<InputProps, 'id' |
'name' |
'value' |
'error' |
'invalid' |
'invalidText' |
'onChange'> & {
  readonly field: FieldPath<TData, FormError, Decimal | null>;
  readonly decimalPlaces?: number;
  readonly decimalScale?: number;
  readonly maxValue?: number;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const DecimalField: <TData>(props: BaseProps<TData>) => React.ReactElement = ({
  field,
  decimalPlaces = DECIMAL_PLACES,
  decimalScale,
  maxValue,
  renderError = renderErrorDefault,
  ...restProps
}) => {
  const { meta, value, error } = useFormField(field);
  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const locale = useSelector(getLocale);
  const intlConfig = useMemo(() => ({ locale }), [locale]);

  const [viewValue, setViewValue] = useState(() => value?.toString() ?? '');

  useEffect(() => {
    try {
      if (viewValue.trim()) {
        const parsed = new Decimal(viewValue);
        if (value === null) {
          setViewValue('');
        } else if (!value.equals(parsed)) {
          setViewValue(value.toString());
        }
      } else if (value !== null) {
        setViewValue(value.toString());
      }
    } catch {
      // noop
    }
  }, [value, viewValue]);

  const handleChange = useCallback((
    _1: unknown,
    _2: unknown,
    values: CurrencyInputOnChangeValues | undefined,
  ) => {
    if (!values) {
      return;
    }

    if (Number(values.float) > (maxValue || Number.MAX_SAFE_INTEGER)) {
      return;
    }

    const currentValue = field.value.get(field.form.currentState);
    const nextValue = values.float !== null
      ? new Decimal(values.float)
      : null;

    setViewValue(values.value);

    if (currentValue && nextValue?.equals(currentValue)) {
      return;
    }

    field.form.change(pipe(
      field.form.currentState,
      field.value.set(nextValue),
    ));
  }, [field, maxValue]);

  const handleBlur = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));
  }, [field]);

  return (
    <CurrencyInput
      {...restProps}
      id={`${field.form.name}.${field.path}`}
      name={field.path}
      value={viewValue}
      data-value={value?.toNumber()}
      // CurrencyInput passes all props to "customInput" (see implementation)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      error={error}
      invalid={invalid}
      invalidText={error && invalid ? renderError(error) : null}
      onValueChange={handleChange}
      onBlur={handleBlur}
      decimalScale={decimalScale}
      decimalsLimit={decimalPlaces}
      allowDecimals={decimalPlaces > 0}
      allowNegativeValue={false}
      disableGroupSeparators={false}
      disableAbbreviations={true}
      formatValueOnBlur={true}
      intlConfig={intlConfig}
      customInput={Input}
    />
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
